import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';

const BRAND_BLUE = '#1E5FB7';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ZusatzfragenData {
  istEigentuemer: boolean;
  eigentuemer: string;
  istFinanziert: boolean;
  finanzierungsbank: string;
  hatRechtsschutz: boolean;
  rechtsschutzVersicherer: string;
  rechtsschutzVsNummer: string;
  istBevollmaechtigter: boolean;
  bevollmaechtigterName: string;
}

interface Props {
  data: ZusatzfragenData;
  onChange: (data: ZusatzfragenData) => void;
  onNext: () => void;
  onBack: () => void;
}

// ─── iOS-style Toggle Card ────────────────────────────────────────────────────

interface ToggleCardProps {
  emoji: string;
  title: string;
  why: string;
  value: boolean;
  onYes: () => void;
  onNo: () => void;
  yesTestId?: string;
  noTestId?: string;
  children?: React.ReactNode;
}

function ToggleCard({ emoji, title, why, value, onYes, onNo, yesTestId, noTestId, children }: ToggleCardProps) {
  return (
    <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start gap-4">
          <span className="text-[28px] leading-none mt-0.5 flex-shrink-0">{emoji}</span>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-gray-900 leading-snug">{title}</h3>
            <p className="text-[12px] text-gray-400 mt-0.5 leading-relaxed">{why}</p>
          </div>
        </div>

        {/* Yes / No toggle buttons */}
        <div className="flex gap-3 mt-4">
          <button
            data-testid={yesTestId}
            onClick={onYes}
            className="flex-1 py-4 rounded-xl text-[15px] font-bold transition-all border-2"
            style={{
              minHeight: 64,
              borderColor: value ? BRAND_BLUE : '#E5E7EB',
              background: value ? BRAND_BLUE : '#F9FAFB',
              color: value ? '#fff' : '#374151',
            }}
          >
            Ja
          </button>
          <button
            data-testid={noTestId}
            onClick={onNo}
            className="flex-1 py-4 rounded-xl text-[15px] font-bold transition-all border-2"
            style={{
              minHeight: 64,
              borderColor: !value ? '#374151' : '#E5E7EB',
              background: !value ? '#374151' : '#F9FAFB',
              color: !value ? '#fff' : '#374151',
            }}
          >
            Nein
          </button>
        </div>
      </div>

      {/* Conditional content */}
      <AnimatePresence>
        {children && (
          <motion.div
            key="conditional"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-5 pb-5 pt-1 border-t border-gray-100 bg-gray-50">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Inline Input ─────────────────────────────────────────────────────────────

function InlineInput({ label, value, onChange, placeholder, optional }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  optional?: boolean;
}) {
  return (
    <div className="mt-3">
      <label className="block text-[12px] font-semibold text-gray-500 mb-1.5">
        {label}{optional && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-[14px] focus:outline-none focus:ring-2 focus:ring-[#1E5FB7]/30 focus:border-[#1E5FB7] transition-all"
        style={{ minHeight: 48 }}
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ZusatzfragenStep({ data, onChange, onNext, onBack }: Props) {
  const set = <K extends keyof ZusatzfragenData>(key: K, val: ZusatzfragenData[K]) =>
    onChange({ ...data, [key]: val });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center mb-6">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md text-[26px]"
          style={{ background: BRAND_BLUE }}
        >
          <span>📋</span>
        </div>
        <h2 className="text-[22px] font-bold text-gray-900">Noch vier kurze Fragen</h2>
        <p className="text-[14px] text-gray-500 mt-1">Dann sind wir fast fertig.</p>
      </div>

      {/* 1 — Eigentümer */}
      <ToggleCard
        emoji="🔐"
        title="Sind Sie selbst Eigentümer/in des Fahrzeugs?"
        why="Wir benötigen dies für die korrekte Zuordnung im Gutachten."
        value={data.istEigentuemer}
        onYes={() => onChange({ ...data, istEigentuemer: true, eigentuemer: '' })}
        onNo={() => set('istEigentuemer', false)}
        yesTestId="toggle-eigentuemer-ja"
        noTestId="toggle-eigentuemer-nein"
      >
        {!data.istEigentuemer && (
          <InlineInput
            label="Eigentümer / Bank / Leasinggeber"
            value={data.eigentuemer}
            onChange={v => set('eigentuemer', v)}
            placeholder="Eigentümer oder Leasinggeber angeben"
          />
        )}
      </ToggleCard>

      {/* 2 — Finanzierung */}
      <ToggleCard
        emoji="💳"
        title="Ist das Fahrzeug finanziert oder geleast?"
        why="Manche Versicherungen zahlen direkt an den Kreditgeber."
        value={data.istFinanziert}
        onYes={() => set('istFinanziert', true)}
        onNo={() => onChange({ ...data, istFinanziert: false, finanzierungsbank: '' })}
        yesTestId="toggle-finanziert-ja"
        noTestId="toggle-finanziert-nein"
      >
        {data.istFinanziert && (
          <InlineInput
            label="Finanzierungsbank oder Leasinggeber"
            value={data.finanzierungsbank}
            onChange={v => set('finanzierungsbank', v)}
            placeholder="Finanzierungsbank oder Leasinggeber"
          />
        )}
      </ToggleCard>

      {/* 3 — Rechtsschutz */}
      <ToggleCard
        emoji="⚖️"
        title="Haben Sie eine Rechtsschutzversicherung?"
        why="Bei Unfällen übernimmt die Rechtsschutz oft anwaltliche Kosten."
        value={data.hatRechtsschutz}
        onYes={() => set('hatRechtsschutz', true)}
        onNo={() => onChange({ ...data, hatRechtsschutz: false, rechtsschutzVersicherer: '', rechtsschutzVsNummer: '' })}
        yesTestId="toggle-rechtsschutz-ja"
        noTestId="toggle-rechtsschutz-nein"
      >
        {data.hatRechtsschutz && (
          <>
            <InlineInput
              label="Versicherer"
              value={data.rechtsschutzVersicherer}
              onChange={v => set('rechtsschutzVersicherer', v)}
              placeholder="Versicherer (z. B. ARAG, Allianz)"
            />
            <InlineInput
              label="VS-Nummer"
              value={data.rechtsschutzVsNummer}
              onChange={v => set('rechtsschutzVsNummer', v)}
              placeholder="VS-Nummer (kann später ergänzt werden)"
              optional
            />
          </>
        )}
      </ToggleCard>

      {/* 4 — Bevollmächtigter */}
      <ToggleCard
        emoji="🤝"
        title="Sind Sie selbst der Bevollmächtigte?"
        why="Wir brauchen die Kontaktperson, die Dokumente unterschreiben darf."
        value={data.istBevollmaechtigter}
        onYes={() => onChange({ ...data, istBevollmaechtigter: true, bevollmaechtigterName: '' })}
        onNo={() => set('istBevollmaechtigter', false)}
        yesTestId="toggle-bevollmaechtigter-ja"
        noTestId="toggle-bevollmaechtigter-nein"
      >
        {!data.istBevollmaechtigter && (
          <>
            <InlineInput
              label="Name des Bevollmächtigten"
              value={data.bevollmaechtigterName}
              onChange={v => set('bevollmaechtigterName', v)}
              placeholder="Name des Bevollmächtigten"
            />
            <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <AlertCircle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-amber-700 leading-relaxed">
                <strong>Hinweis:</strong> Vollmacht-Beleg bitte mitbringen.
              </p>
            </div>
          </>
        )}
      </ToggleCard>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
          style={{ minHeight: 52 }}
        >
          <ArrowLeft size={16} /> Zurück
        </button>
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
          style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
        >
          Weiter <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
